import { useMemo } from "react"
import { appConstants } from "../../appConstants"
import { Project } from "../../dto/project/Project"
import { ProjectCardDto } from "../../dto/project/ProjectCardDto"
import { ProjectCreationDto } from "../../dto/project/ProjectCreationDto"
import { ProjectUpdateDto } from "../../dto/project/ProjectUpdateDto"
import { Department } from "../../enum/departments"
import { ProjectStatusEnum } from "../../enum/projectStatusEnum"
import { streamToBlob } from "../../services/file-service"
import { RequestParam, resolveUrl } from "../../services/http-service"
import { useHttp } from "../use-http"

type BSProjectHook = {
  getProjectsCards(
    status: ProjectStatusEnum,
    actualPage: number,
    limit: number,
    search: string,
    fetchOnlyMyProject: boolean
  ): Promise<ProjectCardDto[]>
  getProject(projectId: string): Promise<Project | undefined>
  getProjectCount(status: ProjectStatusEnum, actualPage: number, search: string, onlyMyProject: boolean): Promise<number>
  countVariants(projectId: string): Promise<number>
  countRseeDocuments(projectId: string): Promise<number>
  countBimModel(projectId: string): Promise<number>
  createProject(projectCreationDto: ProjectCreationDto): Promise<Project>
  updateProject(projectUpdateDto: ProjectUpdateDto): Promise<Project>
  sendCoverImageFile(coverImageFile: File, projectId: string): Promise<Response>
  fetchCoverImage(projectId: string): Promise<Blob | undefined>
  deleteCoverImage(projectId: string): Promise<Response>
  updateProjectStatus(projectId: string, status: ProjectStatusEnum): Promise<Project>
  fetchDepartments(): Promise<Department[]>
}

export function useProject(): BSProjectHook {
  const { get, put, post, postFile, deleteRequest } = useHttp()

  return useMemo(
    () => ({
      getProjectsCards(
        status: ProjectStatusEnum,
        actualPage: number,
        limit: number,
        search: string,
        fetchOnlyMyProject: boolean
      ): Promise<ProjectCardDto[]> {
        const requestParams: RequestParam[] = [
          {
            key: "status",
            value: status,
          },
          {
            key: "limit",
            value: limit.toString(),
          },
          {
            key: "actualPage",
            value: actualPage.toString(),
          },
          {
            key: "search",
            value: search,
          },
        ]
        if (fetchOnlyMyProject) {
          return get(resolveUrl(appConstants.apiEndpoints.USER_PROJECT_CARDS, [], {}), requestParams).then((response) =>
            response.json()
          )
        } else {
          return get(resolveUrl(appConstants.apiEndpoints.ORGANIZATION_PROJECT_CARDS, [], {}), requestParams).then(
            (response) => response.json()
          )
        }
      },
      getProject(projectId: string): Promise<Project | undefined> {
        return get(resolveUrl(appConstants.apiEndpoints.PROJECT, []), [
          {
            key: "projectId",
            value: projectId,
          },
        ]).then((response) => {
          if (response.status === 404) {
            return Promise.resolve(undefined)
          } else {
            return response.json().then((data) => Project.fromDto(data))
          }
        })
      },
      getProjectCount(
        status: ProjectStatusEnum,
        actualPage: number,
        search: string,
        onlyMyProject: boolean
      ): Promise<number> {
        return get(resolveUrl(appConstants.apiEndpoints.COUNT_PROJECT, [], {}), [
          {
            key: "status",
            value: status,
          },
          {
            key: "actualPage",
            value: actualPage.toString(),
          },
          {
            key: "search",
            value: search,
          },
          {
            key: "onlyMyProject",
            value: onlyMyProject,
          },
        ]).then((response) => response.json())
      },
      countVariants(projectId: string): Promise<number> {
        return get(resolveUrl(appConstants.apiEndpoints.COUNT_BEEM_SHOT_VARIANT, [], {}), [
          {
            key: "projectId",
            value: projectId,
          },
        ]).then((response) => response.json())
      },
      countRseeDocuments(projectId: string): Promise<number> {
        return get(resolveUrl(appConstants.apiEndpoints.COUNT_BEEM_PILOT_DOCUMENT, [], {}), [
          {
            key: "projectId",
            value: projectId,
          },
        ]).then((response) => response.json())
      },
      countBimModel(projectId: string): Promise<number> {
        return get(resolveUrl(appConstants.apiEndpoints.COUNT_BIM_MODEL, [], {}), [
          {
            key: "projectId",
            value: projectId,
          },
        ]).then((response) => response.json())
      },
      createProject(projectCreationDto: ProjectCreationDto): Promise<Project> {
        return post(appConstants.apiEndpoints.PROJECT, projectCreationDto)
          .then((response) => response.json())
          .then((data) => Project.fromDto(data))
      },
      updateProject(projectUpdateDto: ProjectUpdateDto): Promise<Project> {
        return put(appConstants.apiEndpoints.PROJECT, projectUpdateDto)
          .then((response) => response.json())
          .then((data) => Project.fromDto(data))
      },
      sendCoverImageFile(coverImageFile: File, projectId: string): Promise<Response> {
        const formData = new FormData()
        formData.append("coverImageFile", coverImageFile)
        formData.append("projectId", projectId)
        return postFile(`${appConstants.apiEndpoints.PROJECT_COVER_IMAGE}`, formData)
      },
      fetchCoverImage(projectId: string): Promise<Blob | undefined> {
        return get(resolveUrl(appConstants.apiEndpoints.PROJECT_CARD_IMAGE, []), [
          {
            key: "projectId",
            value: projectId,
          },
        ])
          .then((res) => streamToBlob(res))
          .catch(() => undefined)
      },
      deleteCoverImage(projectId: string): Promise<Response> {
        return deleteRequest(`${appConstants.apiEndpoints.PROJECT_COVER_IMAGE}`, [
          {
            key: "projectId",
            value: projectId,
          },
        ])
      },
      updateProjectStatus(projectId: string, status: ProjectStatusEnum): Promise<Project> {
        return put(resolveUrl(appConstants.apiEndpoints.PROJECT_STATUS, [], {}), {}, [
          {
            key: "projectId",
            value: projectId,
          },
          {
            key: "status",
            value: status,
          },
        ]).then((response) => response.json())
      },
      fetchDepartments(): Promise<Department[]> {
        return get(resolveUrl(`${appConstants.apiEndpoints.BEEM_SHOT}/department`, [])).then((response) => response.json())
      },
    }),
    [deleteRequest, get, post, postFile, put]
  )
}
